import React from "react";
import {
  AppBar,
  Avatar,
  BottomNavigation,
  BottomNavigationAction,
  Box,
  Button,
  Chip,
  Container,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  Toolbar,
  Typography,
} from "@mui/material";
import AdminPanelSettingsRoundedIcon from "@mui/icons-material/AdminPanelSettingsRounded";
import AnalyticsRoundedIcon from "@mui/icons-material/AnalyticsRounded";
import DashboardRoundedIcon from "@mui/icons-material/DashboardRounded";
import EventAvailableRoundedIcon from "@mui/icons-material/EventAvailableRounded";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import ListAltRoundedIcon from "@mui/icons-material/ListAltRounded";
import LockClockRoundedIcon from "@mui/icons-material/LockClockRounded";
import LockResetRoundedIcon from "@mui/icons-material/LockResetRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import MoreHorizRoundedIcon from "@mui/icons-material/MoreHorizRounded";
import PeopleAltRoundedIcon from "@mui/icons-material/PeopleAltRounded";
import PublicRoundedIcon from "@mui/icons-material/PublicRounded";
import ShareRoundedIcon from "@mui/icons-material/ShareRounded";
import SettingsRoundedIcon from "@mui/icons-material/SettingsRounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import WarehouseRoundedIcon from "@mui/icons-material/WarehouseRounded";
import WorkspacePremiumRoundedIcon from "@mui/icons-material/WorkspacePremiumRounded";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import { useProjects } from "../../../context/ProjectsContext";
import { getProjectBillingAccess } from "../../../utils/projectBillingAccess";
import { normalizeProjectModules } from "../../../utils/projectModules";
import { isSuperAdmin } from "../../../utils/roles";
import { isTrialProject } from "../../../utils/trialOnboarding";
import logo from "../../../images/LogoNavbar1.svg";
import BillingAccessBanner from "../BillingAccessBanner";

const BRAND = { orange: "#F35A3A", dark: "#19242F", bg: "#F6F7FB" };

function mobilePageSx() {
  return {
    "& .MuiPaper-root": { borderRadius: "14px" },
    "& .MuiCard-root": { borderRadius: "14px" },
    "& .MuiCardContent-root": { padding: "14px" },
    "& .MuiCardContent-root:last-child": { paddingBottom: "14px" },
    "& .MuiGrid-container": { marginTop: "-12px", width: "calc(100% + 12px)", marginLeft: "-12px" },
    "& .MuiGrid-item": { paddingTop: "12px", paddingLeft: "12px" },
    "& .MuiTableCell-root": { px: 1, py: .9, fontSize: ".78rem" },
    "& .MuiButton-root": { minHeight: 36 },
    "& .MuiTextField-root .MuiInputBase-root": { fontSize: ".86rem" },
    "& h1, & .MuiTypography-h1": { fontSize: "1.65rem", lineHeight: 1.08 },
    "& h2, & .MuiTypography-h2": { fontSize: "1.5rem", lineHeight: 1.1 },
    "& h3, & .MuiTypography-h3": { fontSize: "1.35rem", lineHeight: 1.12 },
    "& h4, & .MuiTypography-h4": { fontSize: "1.18rem", lineHeight: 1.15 },
    "& h5, & .MuiTypography-h5": { fontSize: "1rem", lineHeight: 1.18 },
    "& .MuiAlert-root": { py: .65, px: 1.15, fontSize: ".82rem" },
  };
}

function routeActive(pathname, path, exact = false) {
  if (exact) return pathname === path;
  return pathname === path || pathname.startsWith(`${path}/`);
}

export default function MobileDashboardShell() {
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const { user, logout } = useAuth();
  const { projects, activeProject, setActiveProject } = useProjects();
  const location = useLocation();
  const navigate = useNavigate();
  const superAdmin = isSuperAdmin(user);
  const modules = normalizeProjectModules(activeProject?.modules);
  const billingAccess = getProjectBillingAccess(activeProject);
  const trialMode = !superAdmin && isTrialProject(activeProject);
  const operationsRestricted = !superAdmin && (billingAccess.isLandingOnly || billingAccess.isSuspended);
  const projectBase = activeProject?.slug ? `/dashboard/proyectos/${activeProject.slug}` : "/dashboard/proyectos";

  const go = React.useCallback((path) => {
    setDrawerOpen(false);
    navigate(path);
  }, [navigate]);

  const handleLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  const projectItems = [
    { key: "landing", label: "Diseñá tu Web", icon: <PublicRoundedIcon />, path: `${projectBase}/pagina`, visible: Boolean(activeProject) && modules.landing },
    { key: "products", label: "Productos y servicios", icon: <Inventory2RoundedIcon />, path: `${projectBase}/productos`, visible: Boolean(activeProject) && modules.products && !modules.system, restricted: operationsRestricted },
    { key: "agenda", label: "Agenda", icon: <EventAvailableRoundedIcon />, path: `${projectBase}/agenda`, visible: Boolean(activeProject) && modules.products, restricted: operationsRestricted },
    { key: "orders", label: "Pedidos", icon: <ListAltRoundedIcon />, path: `${projectBase}/pedidos`, visible: Boolean(activeProject) && modules.orders, restricted: operationsRestricted },
    { key: "customers", label: "CRM de clientes", icon: <GroupsRoundedIcon />, path: `${projectBase}/clientes`, visible: Boolean(activeProject), restricted: operationsRestricted },
    { key: "system", label: "Gestión del negocio", icon: <WarehouseRoundedIcon />, path: `${projectBase}/sistema`, visible: Boolean(activeProject) && (superAdmin || modules.system), restricted: operationsRestricted },
    { key: "stats", label: "Estadísticas web", icon: <AnalyticsRoundedIcon />, path: `${projectBase}/estadisticas`, visible: Boolean(activeProject) && modules.stats, restricted: operationsRestricted },
  ];

  const mainBottomPath = modules.orders
    ? `${projectBase}/pedidos`
    : modules.products
      ? `${projectBase}/productos`
      : "/dashboard/proyectos";
  const mainBottomLabel = modules.orders ? "Pedidos" : modules.products ? "Productos" : "Proyectos";
  const mainBottomIcon = modules.orders ? <ListAltRoundedIcon /> : modules.products ? <Inventory2RoundedIcon /> : <StorefrontRoundedIcon />;
  const webBottomPath = activeProject && modules.landing ? `${projectBase}/pagina` : "/dashboard/proyectos";
  const statsBottomPath = activeProject
    ? (modules.stats ? `${projectBase}/estadisticas` : `${projectBase}/configuracion/general`)
    : "/dashboard/proyectos";

  let bottomValue = "home";
  if (location.pathname.includes("/pagina")) bottomValue = "web";
  else if (location.pathname.includes("/pedidos") || location.pathname.includes("/productos")) bottomValue = "operation";
  else if (location.pathname.includes("/estadisticas")) bottomValue = "stats";
  else if (location.pathname !== "/dashboard") bottomValue = "more";

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: BRAND.bg }}>
      <AppBar position="fixed" elevation={0} sx={{ bgcolor: "rgba(255,255,255,.97)", color: BRAND.dark, borderBottom: "1px solid rgba(25,36,47,.08)", backdropFilter: "blur(12px)" }}>
        <Toolbar sx={{ minHeight: "56px !important", px: 1.25 }}>
          <IconButton size="small" onClick={() => setDrawerOpen(true)} sx={{ width: 38, height: 38 }}><MenuRoundedIcon /></IconButton>
          <Box component="img" src={logo} alt="WebProfit" sx={{ width: 31, height: 31, objectFit: "contain", ml: .6 }} />
          <Box sx={{ minWidth: 0, ml: .8 }}>
            <Typography sx={{ fontWeight: 950, lineHeight: 1, fontSize: 14 }}>WebProfit</Typography>
            <Typography variant="caption" noWrap sx={{ display: "block", maxWidth: 165, color: "rgba(25,36,47,.55)", fontWeight: 800, lineHeight: 1.2 }}>{activeProject?.name || (superAdmin ? "Superpanel" : "Panel cliente")}</Typography>
          </Box>
          <Box sx={{ flex: 1 }} />
          {trialMode ? <Chip size="small" label="PRUEBA" sx={{ height: 23, bgcolor: "rgba(243,90,58,.10)", color: BRAND.orange, fontWeight: 950, fontSize: 10 }} /> : null}
        </Toolbar>
      </AppBar>
      <Toolbar sx={{ minHeight: "56px !important" }} />

      <Drawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{ sx: { width: "88%", maxWidth: 350, border: 0, bgcolor: BRAND.dark, color: "#fff" } }}
      >
        <Box sx={{ minHeight: "100%", display: "flex", flexDirection: "column" }}>
          <Box sx={{ p: 2 }}>
            <Stack direction="row" spacing={1.1} alignItems="center">
              <Box component="img" src={logo} alt="WebProfit" sx={{ width: 38, height: 38, objectFit: "contain" }} />
              <Box><Typography sx={{ fontWeight: 950 }}>WebProfit</Typography><Typography variant="caption" sx={{ color: "rgba(255,255,255,.55)" }}>{superAdmin ? "Superpanel" : "Panel cliente móvil"}</Typography></Box>
            </Stack>
          </Box>

          <Box sx={{ px: 1.5, pb: 1.25 }}>
            <Typography variant="caption" sx={{ color: "rgba(255,255,255,.48)", fontWeight: 950, letterSpacing: .8 }}>PROYECTO ACTIVO</Typography>
            <Select
              fullWidth
              size="small"
              value={activeProject?.slug || ""}
              onChange={(event) => setActiveProject(event.target.value)}
              displayEmpty
              sx={{ mt: .7, color: "#fff", bgcolor: "rgba(255,255,255,.08)", borderRadius: 2, ".MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,.16)" }, ".MuiSvgIcon-root": { color: "#fff" } }}
            >
              <MenuItem value="" disabled>Sin proyectos</MenuItem>
              {projects.map((item) => <MenuItem key={item.id || item.slug} value={item.slug}>{item.name}</MenuItem>)}
            </Select>
          </Box>

          <Divider sx={{ borderColor: "rgba(255,255,255,.09)" }} />
          <List sx={{ px: 1.25, py: 1.25, flex: 1, overflowY: "auto" }}>
            <ListItemButton selected={routeActive(location.pathname, "/dashboard", true)} onClick={() => go("/dashboard")} sx={{ borderRadius: 2.2, mb: .35, color: "#fff", "&.Mui-selected": { bgcolor: BRAND.orange }, "&.Mui-selected:hover": { bgcolor: BRAND.orange } }}>
              <ListItemIcon sx={{ minWidth: 38, color: "inherit" }}><DashboardRoundedIcon /></ListItemIcon><ListItemText primary="Inicio" primaryTypographyProps={{ fontWeight: 900 }} />
            </ListItemButton>
            {superAdmin ? (
              <>
                <ListItemButton selected={routeActive(location.pathname, "/dashboard/superpanel")} onClick={() => go("/dashboard/superpanel")} sx={{ borderRadius: 2.2, mb: .35, color: "#fff", "&.Mui-selected": { bgcolor: BRAND.orange } }}><ListItemIcon sx={{ minWidth: 38, color: "inherit" }}><AdminPanelSettingsRoundedIcon /></ListItemIcon><ListItemText primary="Superpanel" primaryTypographyProps={{ fontWeight: 900 }} /></ListItemButton>
                <ListItemButton selected={routeActive(location.pathname, "/dashboard/usuarios")} onClick={() => go("/dashboard/usuarios")} sx={{ borderRadius: 2.2, mb: .35, color: "#fff", "&.Mui-selected": { bgcolor: BRAND.orange } }}><ListItemIcon sx={{ minWidth: 38, color: "inherit" }}><PeopleAltRoundedIcon /></ListItemIcon><ListItemText primary="Usuarios" primaryTypographyProps={{ fontWeight: 900 }} /></ListItemButton>
                <ListItemButton selected={routeActive(location.pathname, "/dashboard/billing")} onClick={() => go("/dashboard/billing")} sx={{ borderRadius: 2.2, mb: .35, color: "#fff", "&.Mui-selected": { bgcolor: BRAND.orange } }}><ListItemIcon sx={{ minWidth: 38, color: "inherit" }}><WorkspacePremiumRoundedIcon /></ListItemIcon><ListItemText primary="Precios y suscripciones" primaryTypographyProps={{ fontWeight: 900 }} /></ListItemButton>
              </>
            ) : null}
            <ListItemButton selected={routeActive(location.pathname, "/dashboard/proyectos", true)} onClick={() => go("/dashboard/proyectos")} sx={{ borderRadius: 2.2, mb: .35, color: "#fff", "&.Mui-selected": { bgcolor: BRAND.orange } }}><ListItemIcon sx={{ minWidth: 38, color: "inherit" }}><StorefrontRoundedIcon /></ListItemIcon><ListItemText primary={superAdmin ? "Clientes / proyectos" : "Mis proyectos"} primaryTypographyProps={{ fontWeight: 900 }} /></ListItemButton>
            <ListItemButton selected={routeActive(location.pathname, "/dashboard/referidos")} onClick={() => go("/dashboard/referidos")} sx={{ borderRadius: 2.2, mb: .35, color: "#fff", "&.Mui-selected": { bgcolor: BRAND.orange }, "&.Mui-selected:hover": { bgcolor: BRAND.orange } }}><ListItemIcon sx={{ minWidth: 38, color: "inherit" }}><ShareRoundedIcon /></ListItemIcon><ListItemText primary="Red de referidos" primaryTypographyProps={{ fontWeight: 900 }} /></ListItemButton>

            {activeProject ? <Typography variant="caption" sx={{ display: "block", px: 1.3, pt: 1.5, pb: .6, color: "rgba(255,255,255,.42)", fontWeight: 950, letterSpacing: .7 }}>OPERACIÓN</Typography> : null}
            {projectItems.filter((item) => item.visible).map((item) => (
              <ListItemButton key={item.key} selected={location.pathname.includes(item.path.replace(projectBase, ""))} onClick={() => go(item.path)} sx={{ borderRadius: 2.2, mb: .35, color: "#fff", "&.Mui-selected": { bgcolor: BRAND.orange }, "&.Mui-selected:hover": { bgcolor: BRAND.orange } }}>
                <ListItemIcon sx={{ minWidth: 38, color: "inherit" }}>{item.restricted ? <LockClockRoundedIcon /> : item.icon}</ListItemIcon>
                <ListItemText primary={item.label} secondary={item.restricted ? "Pausado por pago" : null} primaryTypographyProps={{ fontWeight: 900 }} secondaryTypographyProps={{ sx: { color: "rgba(255,255,255,.48)", fontSize: ".68rem" } }} />
              </ListItemButton>
            ))}
            {activeProject && billingAccess.needsPaymentAction ? (
              <ListItemButton selected={location.pathname.includes("/configuracion/suscripcion")} onClick={() => go(`${projectBase}/configuracion/suscripcion`)} sx={{ borderRadius: 2.2, mb: .35, color: "#fff", border: "1px solid rgba(243,90,58,.45)", bgcolor: "rgba(243,90,58,.12)", "&.Mui-selected": { bgcolor: BRAND.orange } }}>
                <ListItemIcon sx={{ minWidth: 38, color: "inherit" }}><WorkspacePremiumRoundedIcon /></ListItemIcon>
                <ListItemText primary={billingAccess.isGrace ? "Resolver pago" : "Reactivar funciones"} secondary="La landing sigue visible" primaryTypographyProps={{ fontWeight: 950 }} secondaryTypographyProps={{ sx: { color: "rgba(255,255,255,.52)", fontSize: ".68rem" } }} />
              </ListItemButton>
            ) : null}
            <ListItemButton selected={location.pathname.includes("/configuracion")} onClick={() => go(activeProject ? `${projectBase}/configuracion/general` : "/dashboard/proyectos")} sx={{ borderRadius: 2.2, mb: .35, color: "#fff", "&.Mui-selected": { bgcolor: BRAND.orange } }}><ListItemIcon sx={{ minWidth: 38, color: "inherit" }}><SettingsRoundedIcon /></ListItemIcon><ListItemText primary="Configuración" primaryTypographyProps={{ fontWeight: 900 }} /></ListItemButton>
            {activeProject && modules.landing ? <ListItemButton onClick={() => go("/")} sx={{ borderRadius: 2.2, mb: .35, color: "#fff" }}><ListItemIcon sx={{ minWidth: 38, color: "inherit" }}><PublicRoundedIcon /></ListItemIcon><ListItemText primary="Ver landing" primaryTypographyProps={{ fontWeight: 900 }} /></ListItemButton> : null}
          </List>

          <Box sx={{ p: 1.5, borderTop: "1px solid rgba(255,255,255,.09)" }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.2 }}>
              <Avatar sx={{ width: 36, height: 36, bgcolor: BRAND.orange, fontWeight: 950 }}>{user?.name?.[0]?.toUpperCase() || "W"}</Avatar>
              <Box sx={{ minWidth: 0 }}><Typography noWrap sx={{ fontWeight: 900, fontSize: 14 }}>{user?.name || "Usuario"}</Typography><Typography noWrap variant="caption" sx={{ color: "rgba(255,255,255,.55)" }}>{user?.email}</Typography></Box>
            </Stack>
            <Stack direction="row" spacing={.8}>
              <Button fullWidth size="small" startIcon={<LockResetRoundedIcon />} onClick={() => go("/dashboard/seguridad")} sx={{ color: "#fff", borderColor: "rgba(255,255,255,.2)", textTransform: "none", fontWeight: 850 }} variant="outlined">Clave</Button>
              <Button fullWidth size="small" startIcon={<LogoutRoundedIcon />} onClick={handleLogout} sx={{ color: "#fff", borderColor: "rgba(255,255,255,.2)", textTransform: "none", fontWeight: 850 }} variant="outlined">Salir</Button>
            </Stack>
          </Box>
        </Box>
      </Drawer>

      <Box component="main" className="wp-mobile-dashboard" sx={{ pb: "82px", pt: 1.25, ...mobilePageSx() }}>
        <Container maxWidth={false} disableGutters sx={{ px: 1.25 }}>
          <BillingAccessBanner project={activeProject} />
          <Outlet />
        </Container>
      </Box>

      <Paper elevation={10} sx={{ position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 1190, borderRadius: "16px 16px 0 0 !important", overflow: "hidden", borderTop: "1px solid rgba(25,36,47,.08)" }}>
        <BottomNavigation
          value={bottomValue}
          onChange={(_event, value) => {
            if (value === "more") setDrawerOpen(true);
            else if (value === "home") go("/dashboard");
            else if (value === "web") go(webBottomPath);
            else if (value === "operation") go(mainBottomPath);
            else if (value === "stats") go(statsBottomPath);
          }}
          showLabels
          sx={{ height: 64, "& .MuiBottomNavigationAction-root": { minWidth: 0, px: .3, color: "rgba(25,36,47,.55)" }, "& .Mui-selected": { color: BRAND.orange }, "& .MuiBottomNavigationAction-label": { fontSize: ".66rem", fontWeight: 850 }, "& .MuiBottomNavigationAction-label.Mui-selected": { fontSize: ".68rem" } }}
        >
          <BottomNavigationAction value="home" label="Inicio" icon={<DashboardRoundedIcon />} />
          <BottomNavigationAction value="web" label="Mi web" icon={<PublicRoundedIcon />} />
          <BottomNavigationAction
            value="operation"
            label={mainBottomLabel}
            icon={mainBottomIcon}
          />
          <BottomNavigationAction value="stats" label="Estadísticas" icon={<AnalyticsRoundedIcon />} />
          <BottomNavigationAction value="more" label="Más" icon={<MoreHorizRoundedIcon />} />
        </BottomNavigation>
      </Paper>
    </Box>
  );
}
